import { useState } from "react";
import { AlertTriangle, Info, Sparkles } from "lucide-react";
import { LANGUAGES } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const DEFAULT = { enabled: false, variant: "info", cta_url: "", texts: {} };
const VARIANTS = [
  ["info", "Informacja", Info],
  ["promo", "Promocja", Sparkles],
  ["warning", "Ostrzeżenie", AlertTriangle],
];
const lbl = "text-xs font-semibold text-muted-foreground mb-1.5 block";

export function PopupEditor({ value, onChange }) {
  const popup = { ...DEFAULT, ...(value || {}) };
  const [lang, setLang] = useState("pl");
  const tx = popup.texts?.[lang] || { title: "", body: "", cta_label: "" };

  const setPopup = (patch) => onChange({ ...popup, ...patch });
  const setText = (field, v) => {
    const entry = { ...(popup.texts?.[lang] || {}), [field]: v };
    const texts = { ...(popup.texts || {}) };
    if (!entry.title && !entry.body && !entry.cta_label) delete texts[lang];
    else texts[lang] = entry;
    setPopup({ texts });
  };

  const filledLangs = Object.keys(popup.texts || {}).filter((l) => {
    const e = popup.texts[l];
    return e && (e.title || e.body || e.cta_label);
  });

  return (
    <div data-testid="popup-editor" className="bg-card border border-border rounded-2xl p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-bold text-lg">Wyskakujące okno (popup)</h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-2xl">
            Komunikat wyświetlany klientowi na środku strony głównej (np. promocja, zmiana godzin, zamknięcie). Klient może je zamknąć — nie pokaże się ponownie, dopóki nie zmienisz treści. Wpisz tekst w każdym języku; puste języki pokażą wersję polską.
          </p>
        </div>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer">
          <Switch data-testid="popup-enabled-switch" checked={!!popup.enabled} onCheckedChange={(v) => setPopup({ enabled: v })} />
          <span className="text-sm font-semibold">{popup.enabled ? "Włączone" : "Wyłączone"}</span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>Styl / akcent</label>
          <div className="flex flex-wrap gap-2">
            {VARIANTS.map(([key, label, Icon]) => (
              <button
                key={key} type="button" data-testid={`popup-variant-${key}`} onClick={() => setPopup({ variant: key })}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-2 border transition-colors ${popup.variant === key ? "bg-brand-navy text-white border-brand-navy dark:bg-white dark:text-slate-900" : "border-border hover:bg-muted"}`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={lbl}>Link przycisku (opcjonalnie, wspólny dla języków)</label>
          <Input data-testid="popup-cta-url" value={popup.cta_url || ""} onChange={(e) => setPopup({ cta_url: e.target.value })} placeholder="np. https://... lub #cennik" className="bg-background" />
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {LANGUAGES.map((l) => (
            <button
              key={l.code} type="button" data-testid={`popup-lang-${l.code}`} onClick={() => setLang(l.code)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-2 border transition-colors ${lang === l.code ? "bg-brand-teal text-white border-brand-teal" : "border-border hover:bg-muted"}`}
            >
              {l.label}{filledLangs.includes(l.code) && <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />}
            </button>
          ))}
        </div>
        <div className="space-y-4">
          <div>
            <label className={lbl}>Tytuł ({lang.toUpperCase()})</label>
            <Input data-testid="popup-title-input" value={tx.title || ""} onChange={(e) => setText("title", e.target.value)} placeholder="np. Promocja -20% na start" className="bg-background" />
          </div>
          <div>
            <label className={lbl}>Treść ({lang.toUpperCase()})</label>
            <Textarea data-testid="popup-body-input" rows={4} value={tx.body || ""} onChange={(e) => setText("body", e.target.value)} placeholder="Treść komunikatu…" className="bg-background" />
          </div>
          <div>
            <label className={lbl}>Etykieta przycisku ({lang.toUpperCase()}, opcjonalnie)</label>
            <Input data-testid="popup-cta-label-input" value={tx.cta_label || ""} onChange={(e) => setText("cta_label", e.target.value)} placeholder="np. Zobacz ofertę" className="bg-background" />
          </div>
        </div>
      </div>
    </div>
  );
}
